/**
 * [2026-09-12] A/S 상태·메타데이터(단일 출처). 백엔드 AsRepository.FLOW / as_schema.sql 와 ★정합.
 *   상태 '키'(RECEIVED 등)는 백엔드가 저장하고, 화면 '라벨/설명/아이콘'은 여기서 렌더한다.
 *
 * [2026-09-12 사용자 확정 = 하이브리드 단일 흐름] ★설계 변경 근거 (중요):
 *   당초 안전/환경(도메인)으로 단계를 분기했으나 — 실제 A/S 처리 방식(현장 출동 vs 입고 수리)은 도메인으로 결정되지 않는다.
 *   설치형 제품이라도 회수→입고 수리→재설치가 가능하고, 관리자는 이미 임의 단계로 자유 전이할 수 있다(AsAdminController).
 *   → 따라서 단계 라벨을 두 갈래로 나누지 않고 '하나의 범용 8단계'로 통합. 관리자가 케이스 보고 해당 단계만 취사선택해 진행,
 *      그 단계가 공유 타임라인으로 사용자에게 공지된다. 구체 상황(현장 방문/제품 입고 등)은 각 단계의 관리자 메모로 보완.
 *   ★그 결과: 제품별 휴대형/설치형 분류(base 데이터 없음)·serviceType 분기 불필요. 안전/환경(domain)은 '배지·필터'로만 유지.
 *   변경 금지 트리거: 라벨을 다시 도메인/타입으로 분기하지 말 것(위 근거로 통합 결정). 필요 시 관리자 메모로 케이스 구체화.
 */
export const AS_FLOW = ['RECEIVED', 'ARRIVED', 'DIAGNOSIS', 'QUOTED', 'REPAIR', 'CALIBRATION', 'NOTIFIED', 'COMPLETED'] as const;
export type AsStatus = typeof AS_FLOW[number] | 'ON_HOLD' | 'CANCELED';

// 라인 아이콘(인라인 SVG path d).
const IC_BOX = 'M3 8l9-4 9 4-9 4-9-4zM3 8v8l9 4 9-4V8M12 12v8';                       // 방문·입고(제품 확인)
const IC_DOC = 'M9 3h6l1 3H8l1-3zM6 6h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6zM9 11h6M9 15h4';
const IC_SEARCH = 'M10 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM14.5 14.5L20 20';
const IC_QUOTE = 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M9 13h6M9 17h4';
const IC_WRENCH = 'M14.7 6.3a4 4 0 0 0-5.4 5.2l-5.6 5.6 2.2 2.2 5.6-5.6a4 4 0 0 0 5.2-5.4l-2.6 2.6-1.8-1.8 2.4-2.8z';
const IC_GAUGE = 'M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM12 4v3M12 17v3M4 12h3M17 12h3M12 12l4-2.5';
const IC_BELL = 'M6 8a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6zM10 20a2 2 0 0 0 4 0';
const IC_CHECK = 'M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM8.5 12l2.5 2.5 4.5-5';
const IC_PAUSE = 'M9 8v8M15 8v8M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0z';
const IC_X = 'M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM9 9l6 6M15 9l-6 6';

// [2026-09-12 하이브리드] 단계별 범용 라벨·아이콘·설명·상단 배너 문구(단일 세트 — 도메인/타입 분기 없음).
interface StepMeta { label: string; icon: string; desc: string; headline: string; }
export const AS_STEP: Record<string, StepMeta> = {
	RECEIVED:    { label: '접수', icon: IC_DOC,
		desc: '홈페이지로 A/S 신청이 접수되었습니다.', headline: '접수되었습니다' },
	ARRIVED:     { label: '방문·입고', icon: IC_BOX,
		desc: '담당자가 현장을 방문하거나 제품을 입고받아 상태를 확인합니다.', headline: '방문·입고 확인 중입니다' },
	DIAGNOSIS:   { label: '원인 진단', icon: IC_SEARCH,
		desc: '고장 원인을 점검하고 처리 범위를 판단합니다.', headline: '원인 진단 중입니다' },
	QUOTED:      { label: '견적 안내', icon: IC_QUOTE,
		desc: '수리·부품 비용을 안내하고 승인을 받습니다.', headline: '견적을 안내했습니다 · 승인 대기' },
	REPAIR:      { label: '수리·처리', icon: IC_WRENCH,
		desc: '부품 교체·수리 또는 현장 처리를 진행합니다.', headline: '수리·처리 중입니다' },
	CALIBRATION: { label: '교정·검증', icon: IC_GAUGE,
		desc: '측정 정확도·통신 정상화를 교정하고 검증합니다.', headline: '교정·검증 중입니다' },
	NOTIFIED:    { label: '결과 통보', icon: IC_BELL,
		desc: '처리 내역과 결과를 안내드립니다.', headline: '처리 결과를 통보했습니다' },
	COMPLETED:   { label: '처리 완료', icon: IC_CHECK,
		desc: 'A/S 처리를 완료했습니다.', headline: '처리 완료되었습니다' },
	ON_HOLD:     { label: '보류', icon: IC_PAUSE,
		desc: '사유 확인 후 처리를 재개합니다.', headline: '보류 상태입니다' },
	CANCELED:    { label: '취소', icon: IC_X,
		desc: '취소된 신청입니다.', headline: '취소된 신청입니다' },
};

// 도메인(안전/환경) = 제품 성격 = 배지·필터 용도만(단계 라벨과 무관). 신청 시 제품에서 결정.
export const AS_DOMAIN: Record<string, { label: string }> = {
	SAFETY: { label: '안전' },
	ENV:    { label: '환경' },
};

// [하이브리드] 라벨/설명/아이콘/배너는 단계 키만으로 결정(도메인 인자는 하위호환 위해 받되 무시).
export function asLabel(status: string, _domain?: string): string { return AS_STEP[status]?.label ?? status; }
export function asDesc(status: string, _domain?: string): string { return AS_STEP[status]?.desc ?? ''; }
export function asIcon(status: string, _domain?: string): string { return AS_STEP[status]?.icon ?? ''; }
export function asHeadline(status: string, _domain?: string): string { return AS_STEP[status]?.headline ?? status; }
export const asDomainLabel = (d: string): string => AS_DOMAIN[d]?.label || d;

/** 현재 상태의 스테퍼 진행 인덱스. 완료=끝, 예외(보류/취소)는 -1(스테퍼 밖). */
export function asFlowIndex(status: string): number {
	return (AS_FLOW as readonly string[]).indexOf(status);
}

// ── [2026-09-13 사용자 확정] 케이스별 단계 구성(순서+활성) ──
//   as_request.stage_plan(JSON)에서 파싱. 관리자가 원 클릭으로 활성/비활성, 드래그로 순서 변경(DB 저장).
//   ★백엔드 AsRepository.DEFAULT_STAGE_PLAN 과 정합(기본 활성: 견적안내·교정검증만 비활성).
export interface AsStageItem { key: string; active: boolean; }
const DEFAULT_INACTIVE = ['QUOTED', 'CALIBRATION'];
export const AS_DEFAULT_PLAN: AsStageItem[] = AS_FLOW.map((k) => ({ key: k, active: !DEFAULT_INACTIVE.includes(k) }));

function sanitizePlan(arr: unknown[]): AsStageItem[] {
	const seen = new Set<string>(); const out: AsStageItem[] = [];
	for (const it of arr) {
		const k = (it as { key?: unknown })?.key;
		if (typeof k === 'string' && (AS_FLOW as readonly string[]).includes(k) && !seen.has(k)) {
			seen.add(k); out.push({ key: k, active: !!(it as { active?: unknown }).active });
		}
	}
	// 누락된 단계는 비활성으로 뒤에 채워, 관리자가 항상 8단계 전체를 보고 활성화할 수 있게 한다.
	for (const k of AS_FLOW) if (!seen.has(k)) out.push({ key: k, active: false });
	return out;
}

/** stage_plan(JSON 문자열 또는 배열)을 안전하게 파싱. 실패 시 기본 구성. */
export function parsePlan(raw: unknown): AsStageItem[] {
	if (typeof raw === 'string' && raw.trim()) {
		try { const p = JSON.parse(raw); if (Array.isArray(p)) return sanitizePlan(p); } catch { /* fall through */ }
	}
	if (Array.isArray(raw)) return sanitizePlan(raw);
	return AS_DEFAULT_PLAN.map((x) => ({ ...x }));
}

/** 활성 단계 키 목록(순서 유지) — 스테퍼/타임라인 렌더용. */
export const activeStageKeys = (plan: AsStageItem[]): string[] => plan.filter((p) => p.active).map((p) => p.key);
